import EmailTemplateButtonGroup from '@/components/Drawer/EmailTemplateButtonGroup'
import {useGetEmailTemplateDetails} from '@/components/Drawer/hooks/useEmailTemplateDetails'
import {ROUTES} from '@/config/constants'
import {useAuth} from '@/hooks/useAuth'
import {HtmlPreview} from '@/shared-components/HtmlPreview'
import {Spinner} from '@/shared-components/Spinner'
import {EmailTemplateStatus} from '@/types/EmailTemplate'
import {MessageTemplateDrawerStatusEnum} from '@/types/MessageTemplateDrawerStatusEnum'
import {UserRoleEnumSchema} from '@/types/UserRole'
import {useIntl} from 'react-intl'
import {useNavigate, useParams} from 'react-router-dom'

interface Props {
  updateDrawerStatus: (value: MessageTemplateDrawerStatusEnum) => void
}

export default function EmailTemplateDetails({updateDrawerStatus}: Props): React.JSX.Element {
  const {formatMessage} = useIntl()
  const {id: emailTemplateId} = useParams()
  const navigate = useNavigate()
  const {isFetching, data, error} = useGetEmailTemplateDetails(emailTemplateId)
  const {userRole} = useAuth()
  // Only Admin user can edit the template, and it cannot be edited if it is approved
  const isEditDisabled = userRole !== UserRoleEnumSchema.Values.admin || data?.status === EmailTemplateStatus.APPROVED

  const onEditTemplate = () => {
    navigate(`${ROUTES.MESSAGE_TEMPLATE_EDIT.replace(':id', emailTemplateId || '')}`)
    updateDrawerStatus(MessageTemplateDrawerStatusEnum.createEmailTemplate)
  }

  return (
    <div data-testid="email-template-details-container" className="-mt-1 overflow-auto px-1">
      <div className="mb-4 text-lg font-bold text-text-primary">
        {formatMessage({id: 'EMAIL_TEMPLATE_BUILDER.EMAIL_TEMPLATE_DETAILS_SECTION_TITLE'})}
      </div>
      {isFetching && <Spinner size="medium" data-testid="email-template-details-loading" />}
      {!isFetching && error && (
        <p className="text-md my-16 items-center text-center" data-testid="email-template-details-error">
          {formatMessage({id: 'NOTIFICATION_DETAILS.TEMPLATE_LOADING_ERROR'})}
        </p>
      )}
      {!isFetching && !error && data && (
        <div>
          <div className="border-b border-border-gray">
            <div className="pt-3 text-xs font-bold text-text-secondary">
              {formatMessage({id: 'EMAIL_TEMPLATE_BUILDER.EMAIL_TEMPLATE_NAME'})}
            </div>
            <div className="pb-3 pt-1 text-xs text-text-primary">{data.name}</div>
          </div>
          <div className="border-b border-border-gray">
            <div className="pt-3 text-xs font-bold text-text-secondary">
              {formatMessage({id: 'EMAIL_TEMPLATE_BUILDER.EMAIL_TEMPLATE_CATEGORY'})}
            </div>
            <div className="pb-3 pt-1 text-xs text-text-primary">{data.scope}</div>
          </div>
          <div className="border-b border-border-gray">
            <div className="pt-3 text-xs font-bold text-text-secondary">
              {formatMessage({id: 'NOTIFICATION_BUILDER.SUBJECT_LABEL'})}
            </div>
            <div className="pb-3 pt-1 text-xs text-text-primary">{data.subject}</div>
          </div>
          <div className="pt-3">
            <div className="pb-2 text-xs font-bold text-text-secondary">
              {formatMessage({id: 'EMAIL_TEMPLATE_BUILDER.EMAIL_TEMPLATE_PREVIEW'})}
            </div>
            <HtmlPreview html={data.message} data-testid="email-template-details-preview" />
          </div>
          <EmailTemplateButtonGroup
            testIdPrimary="email-template-edit"
            onClickPrimary={onEditTemplate}
            primaryButtonLabel={formatMessage({id: 'WHATSAPP_TEMPLATE_BUILDER.EDIT_BUTTON_LABEL'})}
            isPrimaryDisabled={isEditDisabled}
          />
        </div>
      )}
    </div>
  )
}
